import React, { useState, useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { formatPrice } from '../../utils';
import { ORDER_STATUS } from '../../constants';
import {
  PlusIcon, CalendarCheckIcon, WalletIcon, AlertIcon, PhoneIcon, NavigationIcon,
  ChevronDownIcon, DownloadIcon, UploadIcon, InfoIcon, ArrowRightIcon
} from '../common/Icons';
import './HomeOverview.css';

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const DAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

const PIPELINE_STEPS = [
  { key: ORDER_STATUS.NEW_ORDER, label: 'Yeni sipariş', tone: 'neutral' },
  { key: ORDER_STATUS.FIRMA, label: 'Firma', tone: 'info' },
  { key: ORDER_STATUS.ATOLYE, label: 'Atölye', tone: 'accent' },
  { key: ORDER_STATUS.MONTAJ, label: 'Montaj', tone: 'violet' },
];

const AGENDA_RANGES = [
  { key: 'today', label: 'Bugün' },
  { key: 'tomorrow', label: 'Yarın' },
  { key: 'week', label: 'Bu hafta' },
];

const METRICS = [
  { key: 'earnings', label: 'Tahsilat', money: true },
  { key: 'net', label: 'Net kâr', money: true },
  { key: 'orders', label: 'Sipariş', money: false },
  { key: 'customers', label: 'Müşteri', money: false },
];

const money = (value) => `₺${Math.round(value || 0).toLocaleString('tr-TR')}`;
const moneyPrecise = (value) => `${formatPrice(value)} ₺`;
const compactMoney = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000000) return `${(value / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1000) return `${Math.round(value / 1000)}B`;
  return `${Math.round(value)}`;
};

const getGreeting = (hour) => {
  if (hour >= 5 && hour < 12) return 'Günaydın';
  if (hour >= 12 && hour < 18) return 'İyi günler';
  if (hour >= 18 && hour < 23) return 'İyi akşamlar';
  return 'İyi geceler';
};

const getFirstName = (user) => {
  const source = user?.displayName || (user?.email || '').split('@')[0] || '';
  const first = source.split(/[\s._-]+/)[0] || '';
  return first ? first.charAt(0).toLocaleUpperCase('tr-TR') + first.slice(1) : '';
};

const parseDateKey = (key) => {
  const [y, m, d] = (key || '').split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const dayDiff = (date, today) => Math.round((date - today) / (1000 * 60 * 60 * 24));

// Yüzde değişim etiketi (karşılaştırılacak değer yoksa gösterilmez)
const Delta = ({ current, previous, suffix = '', asCount = false }) => {
  if (asCount) {
    const diff = current - previous;
    if (!previous && !current) return null;
    const up = diff >= 0;
    return <span className={`ho-delta ${up ? 'is-up' : 'is-down'}`}>{up ? '▲' : '▼'} {Math.abs(diff)}{suffix}</span>;
  }
  if (!previous) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const up = pct >= 0;
  return (
    <span className={`ho-delta ${up ? 'is-up' : 'is-down'}`}>
      {up ? '▲' : '▼'} %{Math.abs(pct).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}{suffix}
    </span>
  );
};

const Sparkline = ({ values }) => {
  const points = values.length > 1 ? values : [0, ...values, 0];
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = 200 / (points.length - 1);
  const coords = points.map((v, i) => `${(i * step).toFixed(1)} ${(36 - ((v - min) / range) * 32).toFixed(1)}`);
  const line = `M${coords.join(' L')}`;
  return (
    <svg className="ho-spark" viewBox="0 0 200 40" preserveAspectRatio="none" aria-hidden="true">
      <path d={`${line} L200 40 L0 40 Z`} className="ho-spark-fill" />
      <path d={line} className="ho-spark-line" />
    </svg>
  );
};

const Skeleton = ({ width = '60%' }) => <span className="ho-skeleton" style={{ width }} />;

const HomeOverview = ({
  user,
  isLoading,
  canCreateCustomer,
  canManageExpenses,
  customerStats,
  revenueStats,
  lastYearStats,
  homeExtras,
  monthlyEarningsTrend,
  monthlyNetEarningsTrend,
  monthlyOrderTrend,
  monthlyCustomerTrend,
  onNavigate,
  onNewCustomer,
  onExport,
  onImport,
  isExporting,
  isImporting,
}) => {
  const [agendaRange, setAgendaRange] = useState('today');
  const [metric, setMetric] = useState('earnings');
  const [detailsOpen, setDetailsOpen] = useState(false);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentMonth = now.getMonth();
  const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const extras = useMemo(() => homeExtras || {}, [homeExtras]);

  // Keşif + montaj tek akış (montaj tarihi/saati = teslimat tarihi/saati)
  const agendaItems = useMemo(() => {
    const items = [];
    const add = (list, type) => (list || []).forEach(entry => {
      const date = parseDateKey(entry.dateKey);
      if (!date) return;
      const diff = dayDiff(date, today);
      items.push({
        id: `${type}-${entry.id}`, type, name: entry.name, phone: entry.phone, address: entry.address,
        date, diff, time: entry.time,
        timePassed: diff === 0 && !!entry.time && entry.time < nowTime,
      });
    });
    add(extras.kesifAgenda, 'kesif');
    add(extras.montajAgenda, 'montaj');
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extras.kesifAgenda, extras.montajAgenda]);

  const overdueItems = agendaItems.filter(i => i.diff < 0);
  const todayItems = agendaItems.filter(i => i.diff === 0);

  const visibleAgenda = useMemo(() => {
    const inRange = agendaItems.filter(i => {
      if (agendaRange === 'today') return i.diff <= 0;
      if (agendaRange === 'tomorrow') return i.diff === 1;
      return i.diff >= 0 && i.diff <= 7;
    });
    return inRange.sort((a, b) =>
      a.diff - b.diff || (a.time || '99:99').localeCompare(b.time || '99:99') || (a.name || '').localeCompare(b.name || '', 'tr')
    );
  }, [agendaItems, agendaRange]);

  const todayKesifCount = todayItems.filter(i => i.type === 'kesif').length;
  const todayMontajCount = todayItems.filter(i => i.type === 'montaj').length;
  const overdueMontajs = overdueItems.filter(i => i.type === 'montaj');
  const overdueKesifs = overdueItems.filter(i => i.type === 'kesif');

  // Göstergeler
  const pipeline = extras.pipeline || {};
  const activeOrderCount = PIPELINE_STEPS.reduce((sum, s) => sum + (pipeline[s.key] || 0), 0);
  const maxPipeline = Math.max(...PIPELINE_STEPS.map(s => pipeline[s.key] || 0), 1);
  const collected = revenueStats.total || 0;
  const remaining = revenueStats.remainingTotal || 0;
  const collectedPct = collected + remaining > 0 ? Math.round((collected / (collected + remaining)) * 100) : 0;
  const earningsSpark = (monthlyEarningsTrend || []).slice(0, currentMonth + 1).map(m => m.earnings);
  const recentMonths = extras.recentCustomerMonths || [];
  const maxRecent = Math.max(...recentMonths.map(m => m.count), 1);

  const todayPayments = [
    { key: 'cash', label: 'Nakit', value: revenueStats.todayCash || 0, tone: 'ink' },
    { key: 'iban', label: 'IBAN', value: revenueStats.todayIban || 0, tone: 'accent' },
    { key: 'card', label: 'Kredi kartı (dün)', value: revenueStats.yesterdayCreditCard || 0, tone: 'muted' },
  ];
  const todayPaymentTotal = todayPayments.reduce((s, p) => s + p.value, 0);

  // Performans grafiği
  const chart = useMemo(() => {
    const series = {
      earnings: { cur: (monthlyEarningsTrend || []).map(m => m.earnings), prev: extras.prevEarnings },
      net: { cur: (monthlyNetEarningsTrend || []).map(m => m.netEarnings), prev: extras.prevNetEarnings },
      orders: { cur: (monthlyOrderTrend || []).map(m => m.count), prev: extras.prevOrders },
      customers: { cur: (monthlyCustomerTrend || []).map(m => m.count), prev: extras.prevCustomers },
    }[metric];
    const prev = series.prev || Array(12).fill(0);
    const data = (monthlyEarningsTrend || []).map((m, i) => ({
      month: m.month,
      cur: i <= currentMonth ? (series.cur[i] || 0) : null,
      prev: prev[i] || 0,
    }));
    const sumTo = (arr) => arr.slice(0, currentMonth + 1).reduce((s, v) => s + (v || 0), 0);
    return { data, yearTotal: sumTo(series.cur), prevTotal: sumTo(prev) };
  }, [metric, monthlyEarningsTrend, monthlyNetEarningsTrend, monthlyOrderTrend, monthlyCustomerTrend, extras, currentMonth]);

  const metricInfo = METRICS.find(m => m.key === metric);
  const formatMetric = (v) => (metricInfo.money ? money(v) : Math.round(v || 0).toLocaleString('tr-TR'));

  const firstName = getFirstName(user);

  return (
    <div className="ho">
      {/* Selamlama */}
      <section className="ho-hero">
        <div className="ho-hero-text">
          <span className="ho-date">{now.getDate()} {MONTH_NAMES[currentMonth]} {now.getFullYear()}, {DAY_NAMES[now.getDay()]}</span>
          <h1 className="ho-title">{getGreeting(now.getHours())}{firstName ? `, ${firstName}` : ''}</h1>
          <p className="ho-summary">
            {isLoading ? 'Günün özeti hazırlanıyor…' : (
              <>
                Bugün <strong>{todayKesifCount} keşif</strong> ve <strong>{todayMontajCount} montaj</strong> planlı.
                {overdueItems.length > 0 && <> <span className="ho-summary-alert">{overdueItems.length} iş gecikmede.</span></>}
              </>
            )}
          </p>
        </div>
        <div className="ho-quick">
          {canManageExpenses && (
            <button type="button" className="ho-btn" onClick={() => onNavigate('expenses')}>
              <WalletIcon size={17} /> Gider ekle
            </button>
          )}
          <button type="button" className="ho-btn" onClick={() => onNavigate('kesif')}>
            <CalendarCheckIcon size={17} /> Keşif listesi
          </button>
          {canCreateCustomer && (
            <button type="button" className="ho-btn ho-btn-primary" onClick={onNewCustomer}>
              <PlusIcon size={17} strokeWidth={2.2} /> Yeni müşteri
            </button>
          )}
        </div>
      </section>

      {/* Gecikme uyarısı */}
      {!isLoading && overdueItems.length > 0 && (
        <section className="ho-alert" role="status">
          <AlertIcon size={18} strokeWidth={2} />
          <span className="ho-alert-text">
            {overdueMontajs.length > 0 && (
              <><strong>{overdueMontajs.length} montaj gecikti:</strong> {overdueMontajs.slice(0, 3).map(d => `${d.name} (${-d.diff} gün)`).join(', ')}{overdueMontajs.length > 3 ? '…' : ''}</>
            )}
            {overdueMontajs.length > 0 && overdueKesifs.length > 0 && <span className="ho-alert-sep"> · </span>}
            {overdueKesifs.length > 0 && <><strong>{overdueKesifs.length} keşif gecikti</strong></>}
          </span>
          <button type="button" className="ho-alert-link" onClick={() => { setAgendaRange('today'); document.getElementById('ho-agenda')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
            Görüntüle <ArrowRightIcon size={15} />
          </button>
        </section>
      )}

      {/* Göstergeler */}
      <section className="ho-kpis" aria-label="Özet göstergeler">
        <article className="ho-card ho-kpi">
          <div className="ho-kpi-head">
            <span className="ho-kpi-label">Bu ay tahsilat</span>
            {!isLoading && <Delta current={revenueStats.thisMonth} previous={extras.lastYearMonthToDateRevenue} />}
          </div>
          <div className="ho-kpi-value">{isLoading ? <Skeleton /> : money(revenueStats.thisMonth)}</div>
          <Sparkline values={earningsSpark} />
          <span className="ho-kpi-foot">
            {extras.lastYearMonthToDateRevenue ? `Geçen yıl aynı dönem: ${money(extras.lastYearMonthToDateRevenue)}` : 'Ödeme tarihine göre'}
          </span>
        </article>

        <article className="ho-card ho-kpi">
          <div className="ho-kpi-head">
            <span className="ho-kpi-label">Tahsil edilecek</span>
            {!isLoading && extras.remainingCustomerCount > 0 && (
              <span className="ho-chip ho-chip-accent">{extras.remainingCustomerCount} müşteri</span>
            )}
          </div>
          <div className="ho-kpi-value">{isLoading ? <Skeleton /> : money(remaining)}</div>
          <div className="ho-progress" aria-hidden="true"><span style={{ width: `${collectedPct}%` }} /></div>
          <span className="ho-kpi-foot">Onaylı siparişlerin %{collectedPct}'i tahsil edildi</span>
        </article>

        <article className="ho-card ho-kpi">
          <div className="ho-kpi-head">
            <span className="ho-kpi-label">Aktif sipariş</span>
            <span className="ho-kpi-hint">teslim edilmemiş</span>
          </div>
          <div className="ho-kpi-value">{isLoading ? <Skeleton width="30%" /> : activeOrderCount}</div>
          <div className="ho-stack" aria-hidden="true">
            {PIPELINE_STEPS.map(s => (pipeline[s.key] || 0) > 0 && (
              <span key={s.key} className={`ho-tone-${s.tone}`} style={{ flexGrow: pipeline[s.key] }} />
            ))}
          </div>
          <div className="ho-legend">
            {PIPELINE_STEPS.map(s => (
              <span key={s.key} className={`ho-legend-item ho-text-${s.tone}`}>● {s.label.split(' ')[0]} {pipeline[s.key] || 0}</span>
            ))}
          </div>
        </article>

        <article className="ho-card ho-kpi">
          <div className="ho-kpi-head">
            <span className="ho-kpi-label">Yeni müşteri · {MONTH_NAMES[currentMonth]}</span>
            {!isLoading && <Delta current={customerStats.thisMonth} previous={lastYearStats.thisMonth} asCount />}
          </div>
          <div className="ho-kpi-value">{isLoading ? <Skeleton width="30%" /> : customerStats.thisMonth}</div>
          <div className="ho-minibars" aria-hidden="true">
            {recentMonths.map((m, i) => (
              <span
                key={`${m.year}-${m.month}`}
                className={i === recentMonths.length - 1 ? 'is-current' : ''}
                style={{ height: `${Math.max((m.count / maxRecent) * 100, 6)}%` }}
                title={`${m.label}: ${m.count}`}
              />
            ))}
          </div>
          <span className="ho-kpi-foot">Geçen yıl {MONTH_NAMES[currentMonth]}: {lastYearStats.thisMonth}</span>
        </article>
      </section>

      {/* Plan + akış */}
      <section className="ho-row">
        <article className="ho-card ho-agenda" id="ho-agenda">
          <div className="ho-card-head">
            <div>
              <h2 className="ho-card-title">Günün planı</h2>
              <span className="ho-card-sub">Keşif ve montajlar tek akışta</span>
            </div>
            <div className="ho-segment" role="group" aria-label="Zaman aralığı">
              {AGENDA_RANGES.map(r => (
                <button
                  key={r.key}
                  type="button"
                  className={agendaRange === r.key ? 'is-active' : ''}
                  aria-pressed={agendaRange === r.key}
                  onClick={() => setAgendaRange(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="ho-agenda-list">
              {[0, 1, 2].map(i => <div key={i} className="ho-agenda-row"><Skeleton width="100%" /></div>)}
            </div>
          ) : visibleAgenda.length === 0 ? (
            <div className="ho-empty">
              <span className="ho-empty-icon"><CalendarCheckIcon size={22} /></span>
              <strong>Planlı iş yok</strong>
              <span>Bu aralıkta keşif veya montaj görünmüyor.</span>
            </div>
          ) : (
            <ul className="ho-agenda-list">
              {visibleAgenda.map(item => {
                const late = item.diff < 0;
                const tone = late || item.timePassed ? 'danger' : item.type === 'kesif' ? 'accent' : 'violet';
                const typeLabel = item.type === 'kesif' ? 'Keşif' : 'Montaj';
                let status = typeLabel;
                if (late) status = `${typeLabel} · ${-item.diff} gün gecikti`;
                else if (item.timePassed) status = `${typeLabel} · saati geçti`;
                return (
                  <li key={item.id} className={`ho-agenda-row ho-row-${tone}`}>
                    <div className="ho-agenda-when">
                      <span className={`ho-agenda-time${item.time ? '' : ' is-empty'}`}>{item.time || '--:--'}</span>
                      {(agendaRange === 'week' || late) && (
                        <span className="ho-agenda-date">{item.date.getDate()} {MONTH_NAMES[item.date.getMonth()].slice(0, 3)} {DAY_SHORT[item.date.getDay()]}</span>
                      )}
                    </div>
                    <span className="ho-agenda-bar" aria-hidden="true" />
                    <div className="ho-agenda-info">
                      <div className="ho-agenda-name-line">
                        <span className="ho-agenda-name">{item.name || 'İsimsiz müşteri'}</span>
                        <span className={`ho-chip ho-chip-${tone}`}>{status}</span>
                      </div>
                      {item.address && <span className="ho-agenda-address">{item.address}</span>}
                    </div>
                    <div className="ho-agenda-actions">
                      {item.phone && (
                        <a className="ho-icon-btn" href={`tel:${item.phone}`} aria-label={`${item.name} ara`} title="Ara">
                          <PhoneIcon size={16} />
                        </a>
                      )}
                      {item.address && (
                        <a
                          className="ho-icon-btn"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${item.name} konumuna git`}
                          title="Yol tarifi"
                        >
                          <NavigationIcon size={16} />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </article>

        <div className="ho-side">
          <article className="ho-card">
            <h2 className="ho-card-title">Sipariş akışı</h2>
            <div className="ho-pipeline">
              {PIPELINE_STEPS.map(s => (
                <div key={s.key} className="ho-pipeline-step">
                  <div className="ho-pipeline-label">
                    <span>{s.label}</span>
                    <strong>{isLoading ? '–' : pipeline[s.key] || 0}</strong>
                  </div>
                  <div className="ho-pipeline-track">
                    <span className={`ho-tone-${s.tone}`} style={{ width: `${((pipeline[s.key] || 0) / maxPipeline) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="ho-card">
            <div className="ho-card-head ho-card-head-tight">
              <h2 className="ho-card-title">Bugünkü tahsilat</h2>
              <span className="ho-card-figure">{isLoading ? '–' : money(todayPaymentTotal)}</span>
            </div>
            <div className="ho-stack ho-stack-lg" aria-hidden="true">
              {todayPaymentTotal > 0
                ? todayPayments.map(p => p.value > 0 && <span key={p.key} className={`ho-tone-${p.tone}`} style={{ flexGrow: p.value }} />)
                : <span className="ho-tone-empty" style={{ flexGrow: 1 }} />}
            </div>
            <dl className="ho-payments">
              {todayPayments.map(p => (
                <div key={p.key}>
                  <dt className={`ho-text-${p.tone}`}>● {p.label}</dt>
                  <dd>{money(p.value)}</dd>
                </div>
              ))}
            </dl>
          </article>
        </div>
      </section>

      {/* Performans */}
      <section className="ho-card ho-perf">
        <div className="ho-card-head">
          <div>
            <h2 className="ho-card-title">Performans · {now.getFullYear()}</h2>
            <span className="ho-card-sub">{now.getFullYear()} ile {now.getFullYear() - 1} aylık karşılaştırma</span>
          </div>
          <div className="ho-segment" role="tablist" aria-label="Metrik">
            {METRICS.map(m => (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={metric === m.key}
                className={metric === m.key ? 'is-active' : ''}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="ho-perf-summary">
          <div>
            <span>Yıl başından bugüne</span>
            <strong>{isLoading ? '–' : formatMetric(chart.yearTotal)}</strong>
          </div>
          <div>
            <span>Geçen yılın aynı aylarına göre</span>
            <strong>{isLoading || !chart.prevTotal ? '–' : <Delta current={chart.yearTotal} previous={chart.prevTotal} />}</strong>
          </div>
          <div className="ho-perf-legend">
            <span><i className="ho-swatch ho-tone-ink" />{now.getFullYear()}</span>
            <span><i className="ho-swatch ho-tone-prev" />{now.getFullYear() - 1}</span>
          </div>
        </div>

        <div className="ho-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart.data} barGap={3} barCategoryGap="22%" margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="rgba(24,24,27,0.06)" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: '#71717A', fontSize: 12 }} />
              <YAxis
                width={44}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#71717A', fontSize: 12 }}
                allowDecimals={false}
                tickFormatter={(v) => (metricInfo.money ? compactMoney(v) : v)}
              />
              <Tooltip
                cursor={{ fill: 'rgba(24,24,27,0.04)' }}
                contentStyle={{ borderRadius: 12, border: '1px solid rgba(24,24,27,0.08)', boxShadow: '0 8px 24px -12px rgba(24,24,27,0.25)', fontSize: 13 }}
                formatter={(value, name) => [metricInfo.money ? moneyPrecise(value) : value, name === 'cur' ? now.getFullYear() : now.getFullYear() - 1]}
              />
              <Bar dataKey="prev" fill="#E4E4E7" radius={[5, 5, 2, 2]} maxBarSize={18} />
              <Bar dataKey="cur" radius={[5, 5, 2, 2]} maxBarSize={18}>
                {chart.data.map((entry, i) => (
                  <Cell key={entry.month} fill={i === currentMonth ? '#EA580C' : '#18181B'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Detaylı istatistikler */}
      <section className={`ho-card ho-details${detailsOpen ? ' is-open' : ''}`}>
        <button type="button" className="ho-details-toggle" onClick={() => setDetailsOpen(o => !o)} aria-expanded={detailsOpen}>
          <span>
            <span className="ho-card-title">Detaylı istatistikler</span>
            <span className="ho-card-sub">Gelir, kâr, müşteri ve geçen yıl rakamlarının tamamı</span>
          </span>
          <span className="ho-details-chevron"><ChevronDownIcon size={18} /></span>
        </button>
        <div className="ho-collapse">
          <div className="ho-collapse-inner">
            <div className="ho-details-body">
              <StatGroup title="Gelir" items={[
                ['Beklenen gelir', moneyPrecise(revenueStats.expectedTotal), 'Tüm siparişler toplamı'],
                ['Toplam gelir', moneyPrecise(revenueStats.total), 'Alınan ödemeler'],
                ['Toplam net gelir', moneyPrecise(revenueStats.netTotal), 'Alınan ödeme − sipariş maliyeti − giderler'],
                ['Potansiyel net kazanç', moneyPrecise(revenueStats.expectedNet), 'Toplam satış − toplam maliyet'],
                ['Kalan ödemeler', moneyPrecise(revenueStats.remainingTotal), 'Tahsil edilecek'],
              ]} />
              <StatGroup title="Dönemsel gelir ve kâr" items={[
                ['Bugün', moneyPrecise(revenueStats.today), `Kâr: ${moneyPrecise(revenueStats.todayNet)}`],
                ['Son 7 gün', moneyPrecise(revenueStats.thisWeek), `Kâr: ${moneyPrecise(revenueStats.thisWeekNet)}`],
                ['Bu ay', moneyPrecise(revenueStats.thisMonth), `Kâr: ${moneyPrecise(revenueStats.thisMonthNet)}`],
                ['Bu yıl', moneyPrecise(revenueStats.thisYear), `Kâr: ${moneyPrecise(revenueStats.thisYearNet)}`],
              ]} />
              <StatGroup title="Müşteriler" items={[
                ['Toplam müşteri', customerStats.total, 'Tüm kayıtlı müşteriler'],
                ['Yeni sipariş durumunda', customerStats.active, 'Onay bekleyen'],
                ['Bugün', customerStats.today, 'Bugün sipariş oluşturan'],
                ['Son 7 gün', customerStats.thisWeek, 'Sipariş oluşturan'],
                ['Bu ay', customerStats.thisMonth, 'Sipariş oluşturan'],
                ['Bu yıl', customerStats.thisYear, 'Sipariş oluşturan'],
              ]} />
              <StatGroup title={`Geçen yıl (${now.getFullYear() - 1})`} items={[
                ['Aynı gün', lastYearStats.today, 'Müşteri sayısı'],
                ['Aynı hafta', lastYearStats.thisWeek, 'Müşteri sayısı'],
                ['Aynı ay', lastYearStats.thisMonth, 'Müşteri sayısı'],
                ['Yıl toplamı', lastYearStats.total, 'Müşteri sayısı'],
              ]} />
            </div>
          </div>
        </div>
      </section>

      {/* Veri yönetimi */}
      <section className="ho-card ho-data">
        <div className="ho-data-text">
          <h2 className="ho-card-title">Veri yönetimi</h2>
          <span className="ho-card-sub">
            <InfoIcon size={14} /> Yedek yükleme mevcut verileri silmez; aynı kayıtlar güncellenir, yeniler eklenir.
          </span>
        </div>
        <div className="ho-data-actions">
          <button type="button" className="ho-btn" onClick={onExport} disabled={isExporting || isLoading}>
            <DownloadIcon size={17} /> {isExporting ? 'Yedekleniyor…' : 'Yedek al'}
          </button>
          <button type="button" className="ho-btn" onClick={onImport} disabled={isImporting || isLoading}>
            <UploadIcon size={17} /> Yedek yükle
          </button>
        </div>
      </section>
    </div>
  );
};

const StatGroup = ({ title, items }) => (
  <div className="ho-statgroup">
    <h3>{title}</h3>
    <div className="ho-statgrid">
      {items.map(([label, value, hint]) => (
        <div key={label} className="ho-stat">
          <span className="ho-stat-label">{label}</span>
          <strong className="ho-stat-value">{value}</strong>
          <span className="ho-stat-hint">{hint}</span>
        </div>
      ))}
    </div>
  </div>
);

export default HomeOverview;
